import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import type { DashboardView } from '@/api/types';
import { BigCTA } from '@/components/BigCTA';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { HotZoneItem } from '@/components/HotZoneItem';
import { ErrorView } from '@/components/ErrorView';
import { FullPageSpinner } from '@/components/Spinner';
import { uploadDraft } from '@/upload-store';

export function HomePage() {
  const nav = useNavigate();
  const [data, setData] = useState<DashboardView | null>(null);
  const [err, setErr] = useState<unknown>(null);

  useEffect(() => {
    let off = false;
    api.getDashboard()
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off) setErr(e); });
    return () => { off = true; };
  }, []);

  if (err) return <div className="page"><ErrorView error={err} /></div>;
  if (!data) return <FullPageSpinner />;

  const startUpload = (type: 'DAILY' | 'HISTORICAL') => {
    uploadDraft.clear();
    uploadDraft.update({ type });
    nav('/upload');
  };

  return (
    <div className="page">
      {/* Balance */}
      <div className="card balance-block">
        <div className="balance-amount">
          {Number(data.balanceRub).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
        </div>
        <div className="balance-label">На балансе</div>
        {Number(data.frozenBalanceRub) > 0 && (
          <div style={{ marginTop: 4, color: 'var(--bk-hint)', fontSize: 12 }}>
            ⏳ {Number(data.frozenBalanceRub).toLocaleString('ru-RU')} ₽ в холде
          </div>
        )}
        <button
          className="big-cta secondary"
          style={{ marginTop: 16 }}
          onClick={() => nav('/wallet')}
        >
          Вывести
        </button>
      </div>

      {/* Trust score */}
      <TrustScoreBadge
        level={data.trustLevel}
        levelLabel={data.trustLevelLabel}
        score={data.trustScore}
      />

      {/* Stats row */}
      <div className="stat-row">
        <div className="stat-tile">
          <div className="value">🔥 {data.streakDays}</div>
          <div className="label">{pluralDays(data.streakDays)} подряд</div>
        </div>
        <div className="stat-tile">
          <div className="value">
            {data.submissionsApproved}/{data.submissionsTotal}
          </div>
          <div className="label">Одобрено отчётов</div>
        </div>
      </div>

      {/* Hot zones preview */}
      {data.hotZones.length > 0 && (
        <>
          <h2 style={{ margin: '8px 0 0', fontSize: 18 }}>🔥 Где сейчас горячо</h2>
          {data.hotZones.map((z, i) => <HotZoneItem key={i} zone={z} rank={i + 1} />)}
        </>
      )}

      {/* Sticky bottom CTAs */}
      <div className="sticky-cta" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <BigCTA onClick={() => startUpload('DAILY')}>
          📸 Загрузить отчёт за сегодня
        </BigCTA>
        <BigCTA variant="secondary" onClick={() => startUpload('HISTORICAL')}>
          Загрузить старые смены
        </BigCTA>
      </div>
    </div>
  );
}

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'дня';
  return 'дней';
}
